import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCircle, CalendarPlus, FileText, Send } from "lucide-react";
import { api, API, fmtINR, fmtDate, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const today = () => new Date().toISOString().slice(0, 10);
const LEAVE_TYPES = ["Casual", "Sick", "Paid", "Unpaid", "Other"];
const ATT_STATUSES = ["Present", "Absent", "Half Day", "Leave", "LWP", "Holiday"];
const badge = (s) => s === "Approved" || s === "Present" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
  : s === "Rejected" || s === "Absent" || s === "LWP" ? "bg-red-50 text-red-700 border-red-200"
  : s === "Pending" || s === "Half Day" ? "bg-amber-50 text-amber-700 border-amber-200"
  : s === "Leave" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-700 border-slate-200";

export default function MyHR() {
  const [month, setMonth] = useState(thisMonth());
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [corrOpen, setCorrOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lf, setLf] = useState({ leave_type: "Casual", from_date: today(), to_date: today(), reason: "" });
  const [cf, setCf] = useState({ attendance_date: today(), requested_status: "Present", reason: "" });

  const load = (m = month) => api.get(`/my/hr?month=${m}`).then((r) => { setD(r.data); setErr(""); })
    .catch((e) => setErr(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const submitLeave = async () => {
    setBusy(true);
    try { await api.post("/my/leaves", lf); toast.success("Leave request submitted"); setLeaveOpen(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const submitCorr = async () => {
    setBusy(true);
    try { await api.post("/my/attendance-corrections", cf); toast.success("Correction request submitted"); setCorrOpen(false); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (err) return <div className="py-24 text-center text-slate-500" data-testid="my-hr-error">{err}</div>;
  if (!d) return <div className="py-24 text-center text-slate-400">Loading your HR data...</div>;

  const sum = d.summary || {};

  return (
    <div data-testid="my-hr">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <UserCircle className="w-6 h-6 text-[#0B132B]" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">My HR</h1>
            <p className="text-sm text-slate-500 mt-0.5">{d.employee.name} · {d.employee.employee_code || "-"} · {d.employee.department || "-"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input data-testid="my-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 bg-white" />
          <Button data-testid="my-apply-leave" onClick={() => setLeaveOpen(true)} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5"><CalendarPlus className="w-4 h-4" /> Apply Leave</Button>
          <Button data-testid="my-request-correction" variant="outline" onClick={() => setCorrOpen(true)} className="rounded-sm gap-1.5"><Send className="w-4 h-4" /> <span className="hidden sm:inline">Correction</span></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="bg-[#0B132B] text-white rounded-lg p-5 shadow-sm" data-testid="my-present-days">
          <div className="text-xs font-semibold uppercase tracking-wider text-white/60">Present ({d.month_label})</div>
          <div className="text-2xl font-mono font-semibold tabular-nums mt-2">{sum.Present || 0}<span className="text-base text-white/40"> / {d.working_days}</span></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="my-absent-days">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Absent / Half / LWP</div>
          <div className="text-2xl font-mono font-semibold tabular-nums text-slate-900 mt-2">{sum.Absent || 0} / {sum["Half Day"] || 0} / {sum.LWP || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="my-leave-days">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Leave Taken (month)</div>
          <div className="text-2xl font-mono font-semibold tabular-nums text-blue-700 mt-2">{sum.Leave || 0}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="my-leave-balance">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Leave Balance</div>
          <div className="text-2xl font-mono font-semibold tabular-nums text-slate-900 mt-2">{d.leave_balance}</div>
        </div>
      </div>

      <Tabs defaultValue="attendance">
        <TabsList className="mb-4">
          <TabsTrigger value="attendance" data-testid="my-tab-attendance">My Attendance</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="my-tab-leaves">My Leaves</TabsTrigger>
          <TabsTrigger value="payslips" data-testid="my-tab-payslips">My Payslips</TabsTrigger>
          <TabsTrigger value="corrections" data-testid="my-tab-corrections">Corrections</TabsTrigger>
        </TabsList>

        <TabsContent value="attendance">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="my-attendance-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">{["Date", "Status", "Remarks", "Marked By"].map((h) => <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
              <tbody>
                {d.attendance.length === 0 ? <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-400">No attendance marked for this month yet</td></tr>
                  : d.attendance.map((a) => (
                    <tr key={a.id} className="border-b border-slate-100"><td className="px-4 py-2.5 text-slate-700">{fmtDate(a.attendance_date)}</td>
                      <td className="px-4 py-2.5"><Badge variant="outline" className={`rounded-sm ${badge(a.status)}`}>{a.status}</Badge></td>
                      <td className="px-4 py-2.5 text-slate-500">{a.remarks || "-"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{a.marked_by || "-"}</td></tr>))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="leaves">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="my-leaves-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">{["Type", "From", "To", "Days", "Reason", "Status", "Reviewed By"].map((h) => <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
              <tbody>
                {d.leaves.length === 0 ? <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No leave requests yet</td></tr>
                  : d.leaves.map((l) => (
                    <tr key={l.id} data-testid={`my-leave-${l.id}`} className="border-b border-slate-100">
                      <td className="px-4 py-2.5 text-slate-700">{l.leave_type}</td><td className="px-4 py-2.5 text-slate-600">{fmtDate(l.from_date)}</td>
                      <td className="px-4 py-2.5 text-slate-600">{fmtDate(l.to_date)}</td><td className="px-4 py-2.5 tabular-nums">{l.days}</td>
                      <td className="px-4 py-2.5 text-slate-500">{l.reason || "-"}</td>
                      <td className="px-4 py-2.5"><Badge variant="outline" className={`rounded-sm ${badge(l.status)}`}>{l.status}</Badge></td>
                      <td className="px-4 py-2.5 text-slate-500">{l.approved_by || "-"}</td></tr>))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payslips">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="my-payslips-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">{["Slip No", "Month", "Present", "Gross", "Deductions", "Net Pay", "Status", ""].map((h) => <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
              <tbody>
                {d.payslips.length === 0 ? <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">No salary slips generated yet</td></tr>
                  : d.payslips.map((p) => (
                    <tr key={p.id} data-testid={`my-payslip-${p.id}`} className="border-b border-slate-100">
                      <td className="px-4 py-2.5 text-slate-700">{p.slip_no}</td><td className="px-4 py-2.5 text-slate-600">{p.month}</td>
                      <td className="px-4 py-2.5 tabular-nums">{p.present_days ?? "-"}</td>
                      <td className="px-4 py-2.5 font-mono tabular-nums">{fmtINR(p.gross_pay)}</td>
                      <td className="px-4 py-2.5 font-mono tabular-nums text-red-600">{fmtINR(p.total_deductions)}</td>
                      <td className="px-4 py-2.5 font-mono tabular-nums font-semibold">{fmtINR(p.net_pay)}</td>
                      <td className="px-4 py-2.5"><Badge variant="outline" className={`rounded-sm ${badge(p.status)}`}>{p.status}</Badge></td>
                      <td className="px-4 py-2.5 text-right">
                        <Button data-testid={`my-slip-pdf-${p.id}`} size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => window.open(`${API}/pdf/salary/${p.id}?token=${localStorage.getItem("token")}`, "_blank")}><FileText className="w-3.5 h-3.5" /> PDF</Button>
                      </td></tr>))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="corrections">
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="my-corrections-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left">{["Date", "Current", "Requested", "Reason", "Status", "Reviewed By"].map((h) => <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}</tr></thead>
              <tbody>
                {d.corrections.length === 0 ? <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">No correction requests yet</td></tr>
                  : d.corrections.map((c) => (
                    <tr key={c.id} data-testid={`my-correction-${c.id}`} className="border-b border-slate-100">
                      <td className="px-4 py-2.5 text-slate-700">{fmtDate(c.attendance_date)}</td>
                      <td className="px-4 py-2.5 text-slate-600">{c.current_status}</td><td className="px-4 py-2.5 text-slate-800 font-medium">{c.requested_status}</td>
                      <td className="px-4 py-2.5 text-slate-500">{c.reason || "-"}</td>
                      <td className="px-4 py-2.5"><Badge variant="outline" className={`rounded-sm ${badge(c.status)}`}>{c.status}</Badge></td>
                      <td className="px-4 py-2.5 text-slate-500">{c.reviewed_by || "-"}</td></tr>))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">Apply for Leave</DialogTitle>
            <DialogDescription>Your request goes to HR for approval. You'll be notified once it's reviewed.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Leave Type</label>
              <Select value={lf.leave_type} onValueChange={(v) => setLf({ ...lf, leave_type: v })}>
                <SelectTrigger data-testid="leave-type" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAVE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">From</label>
                <Input data-testid="leave-from" type="date" value={lf.from_date} onChange={(e) => setLf({ ...lf, from_date: e.target.value })} className="bg-white" /></div>
              <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">To</label>
                <Input data-testid="leave-to" type="date" value={lf.to_date} onChange={(e) => setLf({ ...lf, to_date: e.target.value })} className="bg-white" /></div>
            </div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reason</label>
              <Input data-testid="leave-reason" value={lf.reason} onChange={(e) => setLf({ ...lf, reason: e.target.value })} className="bg-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="leave-submit" onClick={submitLeave} disabled={busy} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={corrOpen} onOpenChange={setCorrOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-head">Attendance Correction Request</DialogTitle>
            <DialogDescription>You cannot edit attendance directly — HR will review this request.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date</label>
              <Input data-testid="corr-date" type="date" value={cf.attendance_date} onChange={(e) => setCf({ ...cf, attendance_date: e.target.value })} className="bg-white" /></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Correct Status</label>
              <Select value={cf.requested_status} onValueChange={(v) => setCf({ ...cf, requested_status: v })}>
                <SelectTrigger data-testid="corr-status" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{ATT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reason</label>
              <Input data-testid="corr-reason" value={cf.reason} onChange={(e) => setCf({ ...cf, reason: e.target.value })} className="bg-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="corr-submit" onClick={submitCorr} disabled={busy} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
